import React, { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import { useOrg } from "./OrgContext";

const API_BASE =
  (process.env.REACT_APP_API_URL || "").replace(/\/$/, "") + "/api";

interface Project {
  id: string;
  name: string;
  orgId: string;
  orgName: string;
  created_at?: string;
}

interface ProjectContextType {
  projects: Project[];
  refreshProjects: () => Promise<void>;
  loading: boolean;
  currentProject: Project | null;
  setCurrentProject: (p: Project | null) => void;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export const ProjectProvider = ({ children }: { children: React.ReactNode }) => {
  const { idToken } = useAuth();
  const { currentOrg, isLoading: orgLoading } = useOrg();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentProject, setCurrentProjectState] = useState<Project | null>(null);

  const setCurrentProject = React.useCallback(
    (p: Project | null) => {
      setCurrentProjectState(p);
      if (idToken) {
        const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "") + "/api";
        fetch(`${API_BASE}/me/preferences`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
          body: JSON.stringify({ lastProjectId: p ? p.id : null }),
        }).catch(() => {});
      }
    },
    [idToken]
  );

  // Dummy state to trigger refresh
  const [refreshKey, setRefreshKey] = useState(0);

  // Only fetch projects when currentOrg is set
  useEffect(() => {
    if (!idToken || !currentOrg) {
      setProjects([]);
      return;
    }
    const fetchProjects = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/orgs/${currentOrg.id}/projects`, {
          headers: { Authorization: `Bearer ${idToken}` },
          credentials: "include",
        });
        if (res.ok) {
          const data = await res.json();
          setProjects(
            data.map((p: any) => ({
              ...p,
              orgId: currentOrg.id,
              orgName: currentOrg.name,
            }))
          );
        } else {
          setProjects([]);
        }
      } catch {
        setProjects([]);
      }
      setLoading(false);
    };
    fetchProjects();
  }, [idToken, currentOrg, refreshKey]);

  // Restore last project for current org
  useEffect(() => {
    const loadPref = async () => {
      if (!idToken || !currentOrg || projects.length === 0) return;
      try {
        const res = await fetch(`${API_BASE}/me/preferences`, {
          headers: { Authorization: `Bearer ${idToken}` },
        });
        if (res.ok) {
          const pref = await res.json();
          // Restore or initialize project preference
          if (pref.lastProjectId) {
            const found = projects.find(
              p => p.id === pref.lastProjectId && p.orgId === currentOrg.id
            );
            if (found) {
              setCurrentProject(found);
            } else if (projects.length > 0) {
              setCurrentProject(projects[0]);
            }
          } else if (projects.length > 0) {
            setCurrentProject(projects[0]);
          }
        }
      } catch {}
    };
    loadPref();
  }, [projects, idToken, currentOrg, setCurrentProject]);

  // Provide a refreshProjects function that returns a Promise
  const refreshProjects = () => {
    setRefreshKey(k => k + 1);
    return Promise.resolve();
  };

  useEffect(() => {
    if (orgLoading) return;

    // Clear project preference when org changes or is unset
    if (!currentOrg) {
      if (currentProject) setCurrentProject(null);
      return;
    }

    if (currentProject && currentProject.orgId !== currentOrg.id) {
      setCurrentProject(null);
    }
  }, [orgLoading, currentOrg, currentProject, setCurrentProject]);

  return (
    <ProjectContext.Provider
      value={{
        projects,
        refreshProjects,
        loading,
        currentProject,
        setCurrentProject,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};

export const useProjects = (): ProjectContextType => {
  const ctx = useContext(ProjectContext);
  if (!ctx) throw new Error("useProjects must be used within ProjectProvider");
  return ctx;
};
